/**
 * THE DECISION LOG — one row per decision, kept whether a rule or a person
 * made it.
 *
 * The run screen answers "what just happened"; this answers "what do we keep,
 * and why is it worth anything later". So it leads with the record's SHAPE —
 * the dark panel — before the rows, because the argument of the screen is
 * that reasoning stored as rules-and-values can be replayed, counted and
 * disagreed with, and reasoning stored as prose cannot.
 *
 * The record panel is a transcription of the real stored document
 * (SyncDecision.interface.m.ts), field for field, including the three fields
 * that are permanently null and the review block. It is documentation, not
 * data: no row's values are shown in it, so there is nothing in it to go
 * stale against a particular booking.
 */

import React, { useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Error from '../../../Components/Error';
import { formatMarginPct, profitToneFor } from '../presentation';
import { decidedByLabel, partnerDocsCopy } from '../decisionPresentation';
import {
  byDecidedAtDesc, outcomeLine, partnerNameFor, settlingRule,
} from './decisionFacts';
import useDecisionFeed, { windowLabel } from './useDecisionFeed';
import DecisionFilters from './Components/DecisionFilters';

/**
 * The stored record, verbatim from the API's own interface.
 *
 * `demand_context`, `model` and the nullable features are shown AS null
 * because that is the point the panel is making: a gap stored as null is
 * countable and a gap stored as an absent key is not. This codebase has paid
 * for that lesson twice.
 */
const RECORD_SHAPE = [
  '{',
  '  reference             "N3115296"',
  '  partner_id            "57c392f7-…"',
  '  decided_at            "2026-10-12T14:52:03.001Z"   // fixed-width UTC',
  '  decided_by            "agent" | "<email>"',
  '  decision              "auto_approved" | "held" | "auto_rejected"',
  '                        | "not_evaluated"            // never rewritten',
  '  held_reason           "margin_below_floor"         // only when held',
  '  resolution            null | "approved" | "rejected"',
  '',
  '  rules_fired           [                            // stops at the first refusal',
  '    { id: "partner_enabled", pass: true }',
  '    { id: "quote_available", pass: true, value: 217.35 }',
  '    { id: "not_duplicate",   pass: true }',
  '    { id: "margin_floor",    pass: false, value: -0.77, threshold: -0.05 }',
  '  ]',
  '',
  '  features              { margin: -0.77, our_price: 217.35,',
  '                          offer: 50, currency: "EUR", lead_days: 6,',
  '                          pax: 2, vehicle: "Standard Car",',
  '                          hour_local: 16, partner_accept_rate: 0.31 }',
  '                        // every one nullable; null means NOT MEASURED',
  '',
  '  demand_context        null    // no peak-date feed exists yet',
  '  model                 null    // no model DECIDES anything',
  '  partner_docs          { consulted: false, reason: "none loaded" }',
  '',
  '  review {                      // the AI second opinion — EVERY booking',
  '    verdict             "concern" | "no_concern" | "unavailable"',
  '    concerns            [{ kind: "child_seat",',
  '                           evidence: "comment asks for a child seat" }]',
  '    model               "eu.anthropic.claude-sonnet-…"',
  '    instructions_version 4',
  '    ms                  820',
  '  }',
  '',
  '  auto_approval_enabled false   // the flag AS IT WAS, not as it is now',
  '  post_steps            [{ id: "flow_stamp", ok: true, detail: "", ms: 12 }]',
  '  overridden_by         null    // set ONLY on a disagreement',
  '  overridden_at         null',
  '}',
];

const AGENT_PILL = { label: 'agent', bg: 'info' };
const HUMAN_PILL = { label: 'a person', bg: 'light', text: 'dark' };

function DecisionLog() {
  const [error, setError] = useState('');
  const feed = useDecisionFeed({ onError: setError });
  const {
    rows, total, sinceDays, isLoading, hasLoadedOnce, partners,
  } = feed;

  const sorted = useMemo(() => [...rows].sort(byDecidedAtDesc), [rows]);

  return (
    <div className="partner-sync auto-approval">
      <Link className="auto-approval-back" to="/partner-sync">← Back to the queue</Link>

      <div className="auto-approval-head">
        <div className="auto-approval-head-main">
          <div className="auto-approval-eyebrow">Partner sync</div>
          <h1 className="auto-approval-title">Decision log</h1>
          <p className="auto-approval-intro">
            One row per decision, kept whether a rule or a person made it.
            Reasoning is stored as the rules that fired and the values they saw
            — not as prose — so it can be replayed, counted and disagreed with.
            That is what makes it worth anything later.
          </p>
        </div>
      </div>

      <Error error={error} setError={setError} />

      <div className="auto-approval-log-intro">
        <div className="auto-approval-record">
          <div className="auto-approval-record-title">What one decision stores</div>
          <p className="auto-approval-record-blurb">
            The features are kept beside the outcome, so a future model trains
            on what was knowable at the time — not on today&apos;s data.
          </p>
          <pre className="auto-approval-record-body">{RECORD_SHAPE.join('\n')}</pre>
        </div>

        <div className="auto-approval-log-notes">
          <section className="auto-approval-panel">
            <h2 className="auto-approval-panel-title">Why features, not prose</h2>
            <p className="mb-0">
              A sentence explaining a decision cannot be counted, grouped or
              replayed. A list of rules with the values they saw can: &quot;show
              me every offer held on margin that a person then approved&quot; is
              a query, and that query is the training signal.
            </p>
          </section>

          <section className="auto-approval-panel auto-approval-panel--outcome">
            <h2 className="auto-approval-panel-title">The disagreement is the label</h2>
            <p className="mb-0">
              When someone overrides a hold, that row becomes a counter-example
              with the features attached. The policy&apos;s own verdict is never
              rewritten by a person acting — that is what keeps the pair
              (what it predicted, what we did) worth learning from.
            </p>
          </section>

          <section className="auto-approval-panel auto-approval-panel--held">
            <h2 className="auto-approval-panel-title">Not yet knowable</h2>
            <p className="mb-0">
              <code>demand_context: null</code>
              {' — our pricing has no date input, so neither holiday nor peak is'
               + ' visible at decision time. Stored as null rather than omitted,'
               + ' so the gap is countable and the day it is filled the old rows'
               + ' stay honest.'}
            </p>
          </section>
        </div>
      </div>

      <DecisionFilters feed={feed} />

      <div className="auto-approval-card">
        <div className="auto-approval-card-head">
          <div className="auto-approval-card-title">Recent decisions</div>
          <div className="auto-approval-card-meta">
            {`${total} ${total === 1 ? 'decision' : 'decisions'} in the ${windowLabel(sinceDays)} · every one replayable`}
          </div>
        </div>

        {isLoading && (
          <div className="text-center my-5 py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3 mb-0">Loading the log…</p>
          </div>
        )}

        {!isLoading && hasLoadedOnce && sorted.length === 0 && (
          <div className="sync-empty">
            <div className="sync-empty-icon"><i className="fa fa-clipboard-list" /></div>
            <h4 className="fw-bold mb-2">No decisions in this window</h4>
            <p className="text-muted mb-0">
              The policy only writes a row when a partner booking arrives, so a
              quiet window is a quiet window — not a sign it is switched off.
            </p>
          </div>
        )}

        {!isLoading && sorted.length > 0 && (
          <div className="auto-approval-table-wrap">
            <table className="auto-approval-table">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Decided by</th>
                  <th scope="col">Rule that settled it</th>
                  <th scope="col" className="is-numeric">Margin</th>
                  <th scope="col">Partner docs</th>
                  <th scope="col">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const features = row.features || {};
                  const settled = settlingRule(row);
                  const docs = partnerDocsCopy(row.partner_docs);
                  const pill = row.decided_by === 'agent' ? AGENT_PILL : HUMAN_PILL;

                  return (
                    <tr key={`${row.reference}-${row.decided_at}`} data-testid={`log-${row.reference}`}>
                      <th scope="row" className="is-mono">
                        <Link to={`/partner-sync/auto-approval/${encodeURIComponent(row.reference)}`}>
                          {row.reference}
                        </Link>
                        <span className="auto-approval-decided-by">
                          {partnerNameFor(row, partners)}
                        </span>
                      </th>
                      <td>
                        <Badge bg={pill.bg} text={pill.text} className="sync-status">
                          {pill.label}
                        </Badge>
                        {row.decided_by !== 'agent' && (
                          <span className="auto-approval-decided-by">
                            {decidedByLabel(row.decided_by)}
                          </span>
                        )}
                      </td>
                      <td>
                        {settled ? (
                          <>
                            <code>{settled.id}</code>
                            {settled.failed && ' failed'}
                          </>
                        ) : '—'}
                      </td>
                      <td className="is-numeric is-mono">
                        <span className={`sync-profit sync-profit--${profitToneFor(features.margin)}`}>
                          {formatMarginPct(features.margin)}
                        </span>
                      </td>
                      <td className={docs.consulted === false ? 'sync-decision-fail-text' : undefined}>
                        {docs.consulted === true ? 'Checked' : 'None loaded'}
                      </td>
                      <td>{outcomeLine(row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="auto-approval-card-foot">
          Partner docs: whether the partner&apos;s own terms were in the
          knowledge base at decision time. A partner with none loaded is one we
          accepted work from without reading what we were agreeing to.
        </div>
      </div>
    </div>
  );
}

export default DecisionLog;
